import { z } from 'zod';

import { type Clock, systemClock } from './clock';

/**
 * A moment somebody types, and why it is checked here rather than being handed
 * to the server to sort out.
 *
 * Two routes take an instant a person chose: `GET
 * /v1/catalogue/items/{id}/prices/at?instant=` — what a thing cost then — and
 * `GET /v1/outlets/{outletId}/stock?asOf=` — what an outlet held then. Both are
 * **strict ISO 8601**. The API refuses `2026-02-30T00:00:00Z` rather than
 * reading it as 2 March, and it is right to: an answer about a different day
 * than the one asked about is a wrong answer that looks like a right one, and
 * the caller is usually holding a receipt or a delivery note.
 *
 * **JavaScript does exactly what the API refuses to.** `new Date('2026-02-30T00:
 * 00:00Z').toISOString()` is `'2026-03-02T00:00:00.000Z'` — no error, no `NaN`,
 * just a different date. So a client that parses the field with `Date` and sends
 * the result has not validated anything; it has quietly rewritten the question.
 * The round-trip check in {@link isoInstantFrom} is what closes that, and it is
 * the reason this is a module with a comment rather than a regular expression
 * inline in a form.
 *
 * ## Local time in, an instant out
 *
 * The field is a `datetime-local`, so what a person types is a **wall-clock time
 * in the zone they are standing in** — which is the same zone `lib/instant.ts`
 * renders rows in, deliberately (`QueuedWrites` sets the precedent and says why:
 * an instant shown in UTC has whoever reads it subtracting five and a half hours
 * to work out which shift it belonged to). Somebody reads "3 Mar 2026, 14:05"
 * off a row and types that, rather than converting it first.
 *
 * The conversion to the UTC instant the API wants happens once, here, on the way
 * out. Nothing else in the app has to know the field is local.
 *
 * ## Why this is in `lib/` rather than beside a screen
 *
 * It was `routes/catalogue/instant.ts`, whose own note said this is what moves
 * when a second screen asks a person for a moment — "whole, with its parser,
 * because the message and the check are the same statement twice and splitting
 * them is how they drift". FE036's stock levels are that second screen, and they
 * are in a different epic: reaching across `routes/catalogue/` for the parser
 * would couple two epics through a module named for one of them, which is the
 * coupling `lib/instant.ts` refuses for the same reason.
 *
 * `routes/tickets/business-date.ts` is deliberately **not** folded in here. That
 * one reads a calendar date and answers whether it exists; this one reads a
 * date-time in the reader's own zone and answers a UTC instant. Merging them
 * would produce a function that sometimes applies a zone and sometimes must not.
 */

/**
 * `YYYY-MM-DDTHH:mm`, with the seconds a `datetime-local` adds when its step
 * allows them.
 *
 * The shape is checked before the calendar so that "you have not filled this in"
 * and "that is not a real date" stay two different sentences — a partially typed
 * field is a normal state and is not an error about February.
 */
const LOCAL_INSTANT_SHAPE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

export const INSTANT_REQUIRED = 'Choose the date and time to look up.';

export const INSTANT_MALFORMED =
  'That is not a real date and time. 30 February is not a date, and the server refuses one rather than reading it as 2 March.';

/**
 * The UTC instant a local date-time names, or `null` if it names none.
 *
 * The check is a round trip rather than a range test on each part: build the
 * date from the components, then ask it what it thinks they are. Anything the
 * platform normalised away — 30 February, month 13, hour 24 — comes back
 * disagreeing with what went in and is refused. Writing the month lengths out by
 * hand would mean owning leap years, which is the same bug one layer down.
 *
 * A two-digit year would also be refused, which is worth knowing: `new
 * Date(50, …)` is 1950, and the round trip catches that too. The regular
 * expression demands four digits, so it is a belt on a brace.
 *
 * A local time that does not exist — the hour a daylight-saving change skips —
 * is refused by the same mechanism. India keeps no daylight saving so it cannot
 * arise at the garden, but a browser set to another zone would be asking about a
 * moment that never happened, and refusing it is the honest answer.
 *
 * **No clock is read here.** `new Date(y, m, d, …)` builds a date out of numbers
 * it was handed; `lib/clock.ts` governs asking what time it is now, and nothing
 * in this file asks.
 */
export function isoInstantFrom(local: string): string | null {
  const parts = LOCAL_INSTANT_SHAPE.exec(local.trim());

  if (parts === null) return null;

  // `noUncheckedIndexedAccess` is on and a capture group can be absent, so every
  // one is defaulted. Only the seconds group can genuinely be missing; the rest
  // matched or `exec` answered null.
  const [, year = '', month = '', day = '', hour = '', minute = '', second = '00'] = parts;

  const at = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  );

  const survived =
    at.getFullYear() === Number(year) &&
    at.getMonth() === Number(month) - 1 &&
    at.getDate() === Number(day) &&
    at.getHours() === Number(hour) &&
    at.getMinutes() === Number(minute) &&
    at.getSeconds() === Number(second);

  return survived ? at.toISOString() : null;
}

/**
 * What a `datetime-local` field shows for the moment a clock is reading.
 *
 * The other direction of {@link isoInstantFrom}, and it is here rather than
 * beside a form for the reason the shape above is: `YYYY-MM-DDTHH:mm` is stated
 * once, and a field prefilled in a format the parser refuses would open invalid
 * before anybody touched it. `toISOString().slice(0, 16)` is the version that
 * looks right and is wrong — it is UTC, so at the garden it prefills a field
 * five and a half hours behind the wall clock beside it.
 *
 * Seconds are omitted deliberately. The field's default `step` shows minutes, so
 * a value carrying seconds is one the control immediately rounds — and the shape
 * accepts either.
 *
 * **Takes a `Clock`**, per Constitution IV and `lib/clock.ts`: this is a read of
 * what time it is now, which is the thing a test has to be able to pin. `new
 * Date(ms)` parses the number the clock handed over and reads nothing itself.
 */
export function localInstantOf(clock: Clock): string {
  const at = new Date(clock());
  const pad = (value: number): string => String(value).padStart(2, '0');

  return `${String(at.getFullYear()).padStart(4, '0')}-${pad(at.getMonth() + 1)}-${pad(
    at.getDate(),
  )}T${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

/**
 * Now, in the shape a `datetime-local` field takes, from the real clock.
 *
 * The bound convenience, in the shape `clock.ts`'s own `instantNow` uses and for
 * the same reason: the injectable form above is what a test pins, and a screen
 * that genuinely means "now" should not thread a provider through the tree to
 * say so. Anything whose *behaviour* turns on the value takes a `Clock`.
 */
export const localInstantNow = (): string => localInstantOf(systemClock);

/**
 * What a field asking for a moment accepts.
 *
 * Kept beside the parser rather than in `lib/field-schemas.ts`: that file is for
 * a rule several *forms* hold about a field they all have — a name, a code — and
 * this is one field's whole definition, message and check together. They are the
 * same statement twice and splitting them is how they drift.
 */
export const localInstantSchema = z
  .string()
  .trim()
  .min(1, INSTANT_REQUIRED)
  .refine((local) => isoInstantFrom(local) !== null, INSTANT_MALFORMED);
